import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Stack, styled, TextField, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useState, type FC } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { z } from 'zod'

import type { UserDetailsDto, UserUpdatePayloadDto } from '@hcr/api/consumer'
import { createUserDetailsQueryKey } from '@hcr/api/consumer'
import { Alert, Autocomplete, ButtonLoading, COLORS, LinkUnstyled, unit } from '@hcr/ui'
import { isNotUndefined, logger } from '@hcr/utils'

import { SENTRY_CONFIG } from '../../../../configs'
import { useUserExistenceQuery, useIdToken, useUserUpdateMutation } from '../../../../hooks'
import { Path } from '../../../../models'
import { createCountryCodes, to } from '../../../../utils'

const BACK_NAVIGATION_PATH = to(Path.Account)

interface AccountEditFormProps {
  setAccountEditFromData: (data: UserUpdatePayloadDto) => void
  setShowEmailConfirmation: (showEmailConfirmation: boolean) => void
  userDetails: UserDetailsDto
}

export const AccountEditForm: FC<AccountEditFormProps> = ({
  setAccountEditFromData,
  setShowEmailConfirmation,
  userDetails,
}: AccountEditFormProps) => {
  const { t } = useTranslation()
  const idToken = useIdToken()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [updatedEmail, setUpdatedEmail] = useState('')

  const userUpdate = useUserUpdateMutation({ idToken: String(idToken) })
  const userEmailExistence = useUserExistenceQuery(
    {
      idToken: String(idToken),
      email: updatedEmail,
    },
    { enabled: false }
  )

  const schema = z.strictObject({
    update_to_email: z.string().email({ message: t('validation.type-email') }),
    country_code: z
      .string()
      .trim()
      .regex(/^\+[0-9]{1,3}$/, { message: t('validation.type-country-code') }),
    phone: z
      .string()
      .trim()
      .min(6, { message: t('validation.digits-count-at-least', { count: 6 }) })
      .max(15, { message: t('validation.digits-count-not-exceed', { count: 15 }) })
      .regex(/^[0-9]{1,}$/, { message: t('validation.type-number') }),
  })

  type Schema = z.infer<typeof schema>

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      country_code: userDetails.country_code,
      update_to_email: userDetails.email,
      phone: userDetails.phone,
    },
  })

  const onSubmit: SubmitHandler<Schema> = async data => {
    try {
      if (data.phone !== userDetails.phone || data.country_code !== userDetails.country_code) {
        await userUpdate.mutateAsync({
          ...data,
          update_to_email: '',
        })
        await queryClient.invalidateQueries({ queryKey: createUserDetailsQueryKey() })
      }

      if (data.update_to_email !== userDetails.email) {
        const userExistance = await userEmailExistence.refetch()

        if (userExistance.isSuccess) {
          if (userExistance.data?.is_existing) {
            return form.setError('update_to_email', {
              type: 'custom',
              message: t('validation.email-address-is-already-in-use'),
            })
          } else {
            setShowEmailConfirmation(true)
            setAccountEditFromData(data)

            return
          }
        }
      }

      void navigate(to(Path.Account))
    } catch {
      logger.error('Failed to edit the user:', data)
    }
  }

  return (
    <>
      <Stack gap={unit(5)}>
        <Section>
          <Typography variant='uppercaseExtraSmall'>{t('account-edit.basic-info')}</Typography>
          <Inputs>
            <TextField
              className={SENTRY_CONFIG.privacy.className}
              value={userDetails.first_name}
              label={t('account-edit.first-name')}
              disabled
            />
            <TextField
              className={SENTRY_CONFIG.privacy.className}
              value={userDetails.last_name}
              label={t('account-edit.last-name')}
              disabled
            />
            <TextField
              className={SENTRY_CONFIG.privacy.className}
              value={userDetails.street_address}
              label={t('account-edit.address')}
              disabled
            />
            <TextField
              className={SENTRY_CONFIG.privacy.className}
              value={userDetails.postal_code}
              label={t('account-edit.zip-code')}
              disabled
            />
            <TextField
              className={SENTRY_CONFIG.privacy.className}
              value={userDetails.city}
              label={t('account-edit.city')}
              disabled
            />
            <TextField
              className={SENTRY_CONFIG.privacy.className}
              value={userDetails.country}
              label={t('account-edit.country')}
              disabled
            />

            <Contact>
              <Typography variant='bodyS'>
                {t('account-edit.name-and-address-info-will-update-automatically-when-changed')}{' '}
              </Typography>
            </Contact>
          </Inputs>
        </Section>
        <Section>
          <Typography variant='uppercaseExtraSmall'>{t('account-edit.email-address')}</Typography>
          <Inputs>
            <Controller
              control={form.control}
              name='update_to_email'
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                  className={SENTRY_CONFIG.privacy.className}
                  value={value}
                  onChange={event => {
                    setUpdatedEmail(event.target.value)
                    onChange(event.target.value)
                  }}
                  label={t('account-edit.email')}
                  error={isNotUndefined(error)}
                  helperText={error?.message}
                  required
                />
              )}
            />
          </Inputs>
        </Section>
        <Section>
          <Typography variant='uppercaseExtraSmall'>{t('account-edit.phone-number')}*</Typography>
          <FormFieldPhone>
            <Controller
              control={form.control}
              name='country_code'
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Autocomplete
                  label={t('account-edit.country-code')}
                  className={SENTRY_CONFIG.privacy.className}
                  errorText={error?.message}
                  options={createCountryCodes()}
                  isRequired
                  onChange={(_, value) => onChange(value)}
                  value={value}
                />
              )}
            />
            <Controller
              control={form.control}
              name='phone'
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  label={t('account-edit.phone-number')}
                  className={SENTRY_CONFIG.privacy.className}
                  inputMode='tel'
                  value={value}
                  onChange={onChange}
                  required
                  error={isNotUndefined(error)}
                  helperText={error?.message}
                  placeholder={t('account-edit.phone-number')}
                />
              )}
            />
          </FormFieldPhone>
          <Typography variant='meta' paddingLeft={unit(3)}>
            {t('account-edit.required-information')}
          </Typography>
        </Section>
      </Stack>
      <Section>
        {userUpdate.isError && (
          <Alert
            severity='error'
            title={t('account-edit.error')}
            description={t('account-edit.sorry-we-encountered-an-error-while-saving-changes')}
          />
        )}
        <ButtonLoading
          onClick={form.handleSubmit(onSubmit)}
          isLoading={form.formState.isSubmitting}
          disabled={!form.formState.isDirty}
          variant='contained'
          color='primary'
        >
          {t('account-edit.save-changes')}
        </ButtonLoading>
        <Button
          component={LinkUnstyled}
          to={BACK_NAVIGATION_PATH}
          disabled={form.formState.isSubmitting}
          variant='text'
          color='primary'
        >
          {t('account-edit.cancel')}
        </Button>
      </Section>
    </>
  )
}

const Section = styled(Stack)`
  gap: ${unit(3)};
`

const Inputs = styled(Stack)`
  gap: ${unit(4)};
`

const FormFieldPhone = styled(Box)`
  display: grid;

  ${props => props.theme.breakpoints.down('md')} {
    grid-template-columns: 35% 65%;
  }

  ${props => props.theme.breakpoints.up('md')} {
    grid-template-columns: 25% 75%;
  }
`

const Contact = styled(Box)`
  background-color: ${COLORS.vaalea['60%']};
  padding: ${unit(4)} ${unit(5)};
`
